import React, { useEffect } from 'react';
import { useKeycloak } from '../contexts/KeycloakContext';
import { getUserRoleCategory, RoleCategory } from '../config/roles';
import ResidentBlockedScreen from './ResidentBlockedScreen';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { initialized, authenticated, login, logout, isOnline, roles } = useKeycloak();

  useEffect(() => {
    if (initialized && !authenticated && isOnline) {
      // Only redirect to login when we know the network is available.
      // Without the isOnline check this fires when offline (initialized=true,
      // authenticated=false) and navigates the Electron window to Keycloak,
      // producing a plain blue background instead of the offline banner.
      login();
    }
  }, [initialized, authenticated, login, isOnline]);

  // Don't show any loading messages, just return null while loading
  if (!initialized || !authenticated) {
    return null;
  }

  // Residents (moradores/proprietários) don't get web/desktop access — they're
  // meant to use the mobile app. Checked via the same "primary role category"
  // logic DashboardRouter already uses to pick a landing page (highest role
  // wins), so a user who also holds a staff role isn't wrongly blocked.
  // roles is set in the same synchronous state batch as authenticated/initialized
  // (KeycloakContext), so it's already populated here — no extra loading state needed.
  if (getUserRoleCategory(roles) === RoleCategory.RESIDENT) {
    return <ResidentBlockedScreen onLogout={logout} />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};
